import type { LayoutDataItems, TypeMapping } from '@wix/thunderbolt-becky-types'
import _ from 'lodash'
import type {
	ExtractedLayoutWithBreakpointRange,
	LayoutWithBreakpointDataAndVariants,
	LayoutWithFullRange,
	Range,
} from '../../types/responsive'

export const DEFAULT_RANGE = { min: 0, max: Number.MAX_SAFE_INTEGER }

const toCloseRange = <T extends LayoutDataItems>(layoutInRange: LayoutWithBreakpointDataAndVariants<T>) => ({
	...layoutInRange,
	breakpointRange: {
		...layoutInRange.breakpointRange,
		min: layoutInRange.breakpointRange.min || DEFAULT_RANGE.min,
		max: layoutInRange.breakpointRange.max || DEFAULT_RANGE.max,
	},
	variants: layoutInRange.variants || [],
})

export const layoutsToCloseRanges = <T extends LayoutDataItems>(
	layoutInRanges: Array<LayoutWithBreakpointDataAndVariants<T>>
) => layoutInRanges.map(toCloseRange)

const toOpenRange = <T extends LayoutDataItems>(layoutInRange: ExtractedLayoutWithBreakpointRange<T>) => {
	const openLayoutInRange: Omit<ExtractedLayoutWithBreakpointRange<T>, 'breakpointRange'> & {
		breakpointRange: Partial<Range>
	} = {
		...layoutInRange,
	}

	if (openLayoutInRange.breakpointRange.min === DEFAULT_RANGE.min) {
		delete openLayoutInRange.breakpointRange.min
	}

	if (openLayoutInRange.breakpointRange.max === DEFAULT_RANGE.max) {
		delete openLayoutInRange.breakpointRange.max
	}

	if (_.isEmpty(openLayoutInRange.variants)) {
		delete openLayoutInRange.variants
	}

	return openLayoutInRange
}

export const toOpenRanges = <T extends LayoutDataItems>(layoutInRanges: Array<ExtractedLayoutWithBreakpointRange<T>>) =>
	layoutInRanges.map(toOpenRange)

export const omitLayoutKeys = <T>(layoutInRange: { layout: T }, propKeys: Array<keyof T>) => {
	const newLayout = { ...layoutInRange.layout }
	_.forEach(propKeys, (propKey) => delete newLayout[propKey])

	return {
		...layoutInRange,
		layout: newLayout,
	}
}

export const filterByLayoutTypeAndBreakpointId = <
	T extends LayoutDataItems,
	K extends LayoutDataItems['type'],
	S extends keyof TypeMapping[K]
>(
	refArrayLayouts: Array<LayoutWithBreakpointDataAndVariants<T>>,
	layoutType: K,
	layoutPropKey: S
) =>
	(refArrayLayouts.filter(
		// We filter by breakpointId in order to remove layouts that were already added
		// in previous extractLayoutToNonCoveredRanges iterations for different properties (hidden / aspectRatio / sticky)
		(layoutInRange) =>
			layoutInRange.layout.type === layoutType &&
			layoutInRange.breakpointId !== undefined &&
			// @ts-ignore - help?
			!_.isUndefined(layoutInRange.layout[layoutPropKey]) // Filter layouts without property definition
	) as unknown) as Array<LayoutWithBreakpointDataAndVariants<TypeMapping[K]>>

export const subtractRange = (range: Range, rangeToSubtract: Range) => {
	const intersection = {
		min: Math.max(range.min, rangeToSubtract.min),
		max: Math.min(range.max, rangeToSubtract.max),
	}

	if (intersection.min > intersection.max) {
		return [range]
	}
	if (range.min === intersection.min && range.max === intersection.max) {
		return []
	}

	if (range.min === intersection.min) {
		return [{ min: rangeToSubtract.max + 1, max: range.max }]
	} else if (range.max === intersection.max) {
		return [{ min: range.min, max: rangeToSubtract.min - 1 }]
	} else {
		return [
			{ min: range.min, max: rangeToSubtract.min - 1 },
			{ min: rangeToSubtract.max + 1, max: range.max },
		]
	}
}

export const getIntersection = (range: Range, rangeToIntersect: Range) => {
	return {
		min: Math.max(range.min, rangeToIntersect.min),
		max: Math.min(range.max, rangeToIntersect.max),
	}
}

export const getTruthTable = (n: number) => {
	const truthTable = []
	for (let i = 0; i < Math.pow(2, n); i++) {
		const boolArr = []
		for (let j = n - 1; j >= 0; j--) {
			boolArr.push(Boolean(i & Math.pow(2, j)))
		}
		truthTable.push(boolArr)
	}

	return truthTable.reverse()
}

export const addOpposingRangesByTruthTable = <T extends LayoutDataItems>(
	truthTable: Array<Array<boolean>>,
	visitedRanges: Array<LayoutWithFullRange<T>>
) => {
	const newRangesWithoutProperty: Array<Array<Array<Range>>> = []

	truthTable.forEach((row) => {
		const newRangesPerRow: Array<Array<Range>> = []
		visitedRanges.forEach((rangeAndVariantsObj, i) => {
			// rangeAndVariantsObj.breakpointRange here is definitely a single range
			// since it comes from the original layouts
			newRangesPerRow.push(
				row[i]
					? [rangeAndVariantsObj.breakpointRange]
					: subtractRange(DEFAULT_RANGE, rangeAndVariantsObj.breakpointRange)
			)
		})

		newRangesWithoutProperty.push(newRangesPerRow)
	})

	return newRangesWithoutProperty
}
